import React, { useState, useEffect } from 'react';
import { Bell, Check, Eye, X, Settings } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { wsService } from '../services/WebSocketService';

interface Notification {
  id: string;
  type: string;
  submissionId?: string;
  propertyAddress?: string;
  status?: string;
  message: string;
  updatedBy: string;
  isRead: boolean;
  createdAt: string;
  link?: string; // Link to navigate to (e.g., /admin/dfpb-orders/{id})
  order_id?: string; // DFPB order ID
}

interface NotificationBellProps {
  onOpenSettings?: () => void;
}

export function NotificationBell({ onOpenSettings }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();

    // Listen for real-time WebSocket notifications
    const handleNotification = () => {
      fetchNotifications();
    };

    wsService.on('notification', handleNotification);

    // Refresh notifications every 30 seconds as fallback
    const interval = setInterval(fetchNotifications, 30000);

    return () => {
      wsService.off('notification', handleNotification);
      clearInterval(interval);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) return;

      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) throw new Error('VITE_API_URL not configured');
      const response = await fetch(`${apiUrl}/notifications`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsReadNoRefetch = async (notificationId: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.error('No ID token found');
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) throw new Error('VITE_API_URL not configured');
      const response = await fetch(`${apiUrl}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log(`Successfully marked notification ${notificationId} as read`);
      } else if (response.status === 404) {
        console.log(`Notification ${notificationId} not found in database (already deleted)`);
      } else {
        const errorData = await response.json();
        console.error('Failed to mark notification as read:', errorData);
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAsRead = async (notificationId: string) => {
    await markAsReadNoRefetch(notificationId);
    // Refetch to ensure consistency with backend
    await fetchNotifications();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative">
      {/* Bell Icon with Badge */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          const opening = !showPanel;
          setShowPanel(opening);
          if (opening) fetchNotifications();
        }}
        className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setShowPanel(false);
            }}
          />

          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-30 max-h-[500px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
                <p className="text-xs text-gray-500">{unreadCount} unread</p>
              </div>
              <div className="flex items-center gap-2">
                {onOpenSettings && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPanel(false);
                      onOpenSettings();
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title="Notification Settings"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPanel(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {loading && notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tdb-accent mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => {
                    // Determine if notification is clickable based on link or submissionId
                    const isClickable = !!(notification.link || notification.submissionId);

                    const handleNotificationClick = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (!isClickable) return;

                      // Parse the link to determine navigation
                      if (notification.link) {
                        const link = notification.link;

                        // Handle DFPB orders: /admin/dfpb-orders/{id}
                        if (link.startsWith('/admin/dfpb-orders/')) {
                          const orderId = link.split('/').pop();
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'admin-portal',
                              params: { orderId, tab: 'dfpb-orders' }
                            }
                          }));
                        }
                        // Handle admin JV submissions: /admin/jv-submissions/{id} or /admin/jv-submissions/{id}#comments
                        else if (link.startsWith('/admin/jv-submissions/')) {
                          const lastPart = link.split('/').pop() || '';
                          const submissionId = lastPart.split('#')[0];
                          const scrollToComments = link.includes('#comments');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'admin-portal',
                              params: { submissionId, tab: 'jv-submissions', scrollToComments }
                            }
                          }));
                        }
                        // Handle user-facing JV submissions: /jv-submissions/{id} or /jv-submissions/{id}#comments
                        else if (link.startsWith('/jv-submissions/')) {
                          const parts = link.split('/');
                          const lastPart = parts.pop() || '';
                          // Remove hash fragment if present (e.g., "id#comments" -> "id")
                          const submissionId = lastPart.split('#')[0];
                          const scrollToComments = link.includes('#comments');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'jv-portal',
                              params: { submissionId, scrollToComments }
                            }
                          }));
                        }
                        // Handle admin users page: /admin/users or /admin/users?email={email}
                        else if (link.startsWith('/admin/users')) {
                          const url = new URL(link, 'http://dummy.com');
                          const emailParam = url.searchParams.get('email');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'admin-portal',
                              params: { tab: 'users', searchEmail: emailParam }
                            }
                          }));
                        }
                        // Handle tax lists admin: /admin/tax-lists or /admin/tax-lists/{id}
                        else if (link.startsWith('/admin/tax-lists')) {
                          const taxOrderId = link.split('/').pop();
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'taxlatesdata-admin',
                              params: taxOrderId && taxOrderId !== 'tax-lists' ? { taxOrderId, tab: 'orders' } : { tab: 'orders' }
                            }
                          }));
                        }
                        // Handle user-facing tax delinquent lists: /tax-delinquent-lists or /tax-delinquent-lists?orderId={id}
                        else if (link.startsWith('/tax-delinquent-lists')) {
                          const url = new URL(link, 'http://dummy.com');
                          const orderIdParam = url.searchParams.get('orderId');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'tax-delinquent-lists',
                              params: orderIdParam ? { orderId: orderIdParam } : {}
                            }
                          }));
                        }
                        // Handle admin tax delinquent lists: /admin/tax-delinquent-lists or /admin/tax-delinquent-lists?orderId={id}
                        else if (link.startsWith('/admin/tax-delinquent-lists')) {
                          const url = new URL(link, 'http://dummy.com');
                          const orderIdParam = url.searchParams.get('orderId');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'taxlatesdata-admin',
                              params: orderIdParam ? { orderId: orderIdParam, tab: 'orders' } : { tab: 'orders' }
                            }
                          }));
                        }
                        // Handle taxlatesdata-admin: /taxlatesdata-admin or /taxlatesdata-admin?orderId={id}
                        else if (link.startsWith('/taxlatesdata-admin')) {
                          const url = new URL(link, 'http://dummy.com');
                          const orderIdParam = url.searchParams.get('orderId');
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'taxlatesdata-admin',
                              params: orderIdParam ? { orderId: orderIdParam } : {}
                            }
                          }));
                        }
                        // Handle JV Portal invitation: /jv-portal
                        else if (link === '/jv-portal' || link.startsWith('/jv-portal?')) {
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'jv-portal',
                              params: {}
                            }
                          }));
                        }
                        // Handle Tax Delinquent Lists: /tax-delinquent-lists
                        else if (link === '/tax-delinquent-lists' || link.startsWith('/tax-delinquent-lists?')) {
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'tax-delinquent-lists',
                              params: {}
                            }
                          }));
                        }
                        // Generic fallback: try to navigate to the view name from the link
                        else {
                          console.log('Unhandled notification link:', link);
                        }
                      }
                      // Fallback to submissionId if no link
                      else if (notification.submissionId) {
                        window.dispatchEvent(new CustomEvent('navigate', {
                          detail: {
                            view: 'admin-portal',
                            params: {
                              submissionId: notification.submissionId,
                              tab: 'jv-submissions'
                            }
                          }
                        }));
                      }

                      setShowPanel(false);
                      markAsReadNoRefetch(notification.id);
                    };

                    return (
                      <div
                        key={notification.id}
                        onClick={handleNotificationClick}
                        className={`p-4 hover:bg-gray-50 transition-colors ${
                          !notification.isRead ? 'bg-blue-50/50' : ''
                        } ${isClickable ? 'cursor-pointer' : ''}`}
                      >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.message}
                          </p>
                          {notification.propertyAddress && (
                            <p className="text-xs text-gray-500 mt-1">
                              {notification.propertyAddress}
                            </p>
                          )}
                          <div className="flex items-center space-x-3 mt-2">
                            <p className="text-xs text-gray-400">
                              {formatTimestamp(notification.createdAt)}
                            </p>
                            <p className="text-xs text-gray-400">
                              By: {notification.updatedBy}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent parent div onClick
                                markAsRead(notification.id);
                              }}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                              title="Mark as read"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {isClickable && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent parent div onClick
                                handleNotificationClick();
                              }}
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={async () => {
                    // Mark all as read efficiently
                    const unreadNotifications = notifications.filter(n => !n.isRead);
                    await Promise.all(unreadNotifications.map(n => markAsReadNoRefetch(n.id)));
                    // Refetch once at the end
                    await fetchNotifications();
                  }}
                  className="text-xs text-tdb-accent hover:text-tdb-accent/80 font-semibold"
                >
                  Mark all as read
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
